/**
 * Workspace модуля чата: список диалогов, сообщения, E2EE, GIF, пароль на файл.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, MessageSquare, Paperclip, Send, Users, X } from "lucide-react";
import Header from "@/components/Header";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { useToast } from "@/hooks/use-toast";
import { useUser } from "@/hooks/useAuthV2";
import {
  chatApi,
  chatWsUrl,
  type ChatConversation,
  type ChatDirectoryUser,
  type ChatMessage,
} from "@/services/chat-api.service";
import { chatCrypto } from "@/lib/chat-crypto";
import { cn } from "@/lib/utils";

function newUuid(): string {
  return crypto.randomUUID();
}

export default function ChatWorkspacePage() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: me } = useUser();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [peerId, setPeerId] = useState<string | undefined>(undefined);
  const [groupTitle, setGroupTitle] = useState("");
  const [groupMemberIds, setGroupMemberIds] = useState<string[]>([]);
  const [directorySearch, setDirectorySearch] = useState("");
  const [decoded, setDecoded] = useState<Record<string, string>>({});
  const [protectPassword, setProtectPassword] = useState(false);
  const [filePassword, setFilePassword] = useState("");
  const [gifTag, setGifTag] = useState("");

  const directoryQuery = useQuery({
    queryKey: ["chat", "directory", directorySearch],
    queryFn: () =>
      chatApi.directoryUsers({
        q: directorySearch.trim() || undefined,
        limit: 50,
      }),
    staleTime: 30_000,
  });

  const userById = useMemo(() => {
    const map = new Map<string, ChatDirectoryUser>();
    for (const u of directoryQuery.data ?? []) map.set(u.id, u);
    return map;
  }, [directoryQuery.data]);

  const userOptions: ComboboxOption[] = useMemo(
    () =>
      (directoryQuery.data ?? []).map((u) => ({
        value: u.id,
        label: u.username ? `${u.display_name} (@${u.username})` : u.display_name,
        shortLabel: u.display_name,
      })),
    [directoryQuery.data],
  );

  const convQuery = useQuery({
    queryKey: ["chat", "conversations"],
    queryFn: () => chatApi.conversations(),
  });

  const messagesQuery = useQuery({
    queryKey: ["chat", "messages", activeId],
    enabled: Boolean(activeId),
    queryFn: () => chatApi.syncMessages(activeId!, 0, 200),
  });

  const gifsQuery = useQuery({
    queryKey: ["chat", "gifs", gifTag],
    queryFn: () => chatApi.listGifs(gifTag || undefined),
  });

  const active = useMemo(
    () => convQuery.data?.find((c) => c.id === activeId) ?? null,
    [convQuery.data, activeId],
  );

  useEffect(() => {
    void (async () => {
      try {
        const bundle = await chatCrypto.ensureDevice();
        await chatApi.registerDevice({
          device_label: navigator.userAgent.slice(0, 120),
          identity_key_b64: bundle.identityPublicB64,
          signed_prekey_b64: bundle.signedPrekeyPublicB64,
          signed_prekey_sig_b64: bundle.signedPrekeySigB64,
          signed_prekey_id: bundle.signedPrekeyId,
          registration_id: bundle.registrationId,
          one_time_prekeys: bundle.oneTimePrekeys,
        });
      } catch (e) {
        console.warn("chat device register", e);
      }
    })();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) return;
    const ws = new WebSocket(chatWsUrl(token));
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data as string);
        if (data?.type === "chat.message.created") {
          void qc.invalidateQueries({ queryKey: ["chat", "messages"] });
          void qc.invalidateQueries({ queryKey: ["chat", "conversations"] });
        }
      } catch {
        /* ignore */
      }
    };
    const ping = window.setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send("ping");
    }, 25000);
    return () => {
      window.clearInterval(ping);
      ws.close();
    };
  }, [qc]);

  useEffect(() => {
    void (async () => {
      if (!active || !messagesQuery.data?.items) return;
      const participants = active.participants.map((p) => p.user_id).sort();
      if (!(await chatCrypto.hasConversationKey(active.id))) {
        if (active.type === "group") {
          await chatCrypto.rotateSenderKey(active.id, active.sender_key_epoch);
        } else {
          await chatCrypto.bootstrapConversationKey(active.id, participants);
        }
      }
      const next: Record<string, string> = {};
      for (const m of messagesQuery.data.items) {
        try {
          next[m.id] = await chatCrypto.decryptText(m.ciphertext_b64, active.id);
        } catch {
          next[m.id] = "🔒 Не удалось расшифровать (нет ключа / другое устройство)";
        }
      }
      setDecoded(next);
    })();
  }, [active, messagesQuery.data]);

  const ensureKey = useCallback(async (c: ChatConversation) => {
    const participants = c.participants.map((p) => p.user_id).sort();
    if (c.type === "group") {
      await chatCrypto.rotateSenderKey(c.id, c.sender_key_epoch);
    } else {
      await chatCrypto.bootstrapConversationKey(c.id, participants);
    }
  }, []);

  const sendMut = useMutation({
    mutationFn: async () => {
      if (!active || !draft.trim()) return;
      await ensureKey(active);
      const ciphertext_b64 = await chatCrypto.encryptText(draft.trim(), active.id);
      await chatApi.sendMessage(active.id, {
        client_message_id: newUuid(),
        ciphertext_b64,
        content_type: "text",
      });
    },
    onSuccess: () => {
      setDraft("");
      void qc.invalidateQueries({ queryKey: ["chat", "messages", activeId] });
    },
    onError: (e: Error) => toast({ title: "Ошибка отправки", description: e.message, variant: "destructive" }),
  });

  const createDirectMut = useMutation({
    mutationFn: async () => {
      if (!peerId) throw new Error("Выберите пользователя");
      return chatApi.createDirect(peerId);
    },
    onSuccess: async (c) => {
      await ensureKey(c);
      setActiveId(c.id);
      setPeerId(undefined);
      void qc.invalidateQueries({ queryKey: ["chat", "conversations"] });
    },
    onError: (e: Error) =>
      toast({ title: "Не удалось создать чат", description: e.message, variant: "destructive" }),
  });

  const createGroupMut = useMutation({
    mutationFn: async () => chatApi.createGroup(groupTitle.trim(), groupMemberIds),
    onSuccess: async (c) => {
      await ensureKey(c);
      setActiveId(c.id);
      setGroupTitle("");
      setGroupMemberIds([]);
      void qc.invalidateQueries({ queryKey: ["chat", "conversations"] });
    },
    onError: (e: Error) =>
      toast({ title: "Не удалось создать группу", description: e.message, variant: "destructive" }),
  });

  const directTitle = useCallback(
    (c: ChatConversation) => {
      const peer = c.participants.find((p) => p.user_id !== me?.id);
      if (!peer) return "Личный чат";
      const known = userById.get(peer.user_id);
      return known?.display_name ?? peer.user_id.slice(0, 8) + "…";
    },
    [me?.id, userById],
  );

  const toggleGroupMember = (userId: string) => {
    setGroupMemberIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  };

  const onPickFile = async (file: File | null) => {
    if (!file || !active) return;
    try {
      await ensureKey(active);
      let body = await file.arrayBuffer();
      const form = new FormData();
      let metaNote = `📎 ${file.name}`;
      if (protectPassword) {
        if (!filePassword) {
          toast({
            title: "Укажите пароль",
            description: "Пароль не передаётся через чат. Передайте его получателю отдельно.",
            variant: "destructive",
          });
          return;
        }
        const wrapped = await chatCrypto.wrapFileWithPassword(body, filePassword);
        body = wrapped.ciphertext;
        form.append("password_protected", "true");
        form.append("kdf_alg", wrapped.kdf_alg);
        form.append("kdf_salt_b64", wrapped.kdf_salt_b64);
        form.append("kdf_params_json", JSON.stringify(wrapped.kdf_params));
        form.append("file_nonce_b64", wrapped.file_nonce_b64);
        metaNote += " (защищён паролем)";
      } else {
        form.append("password_protected", "false");
      }
      const ciphertext_b64 = await chatCrypto.encryptText(metaNote, active.id);
      const msg = await chatApi.sendMessage(active.id, {
        client_message_id: newUuid(),
        ciphertext_b64,
        content_type: "file",
      });
      form.append("message_id", msg.id);
      form.append("mime_type_declared", file.type || "application/octet-stream");
      form.append("file", new Blob([body], { type: "application/octet-stream" }), file.name);
      await chatApi.uploadAttachment(form);
      setFilePassword("");
      setProtectPassword(false);
      void qc.invalidateQueries({ queryKey: ["chat", "messages", activeId] });
      toast({ title: "Файл отправлен (ciphertext)" });
    } catch (e) {
      toast({
        title: "Ошибка файла",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    }
  };

  const sendGif = async (gifId: string) => {
    if (!active) return;
    await ensureKey(active);
    const ciphertext_b64 = await chatCrypto.encryptText(`gif:${gifId}`, active.id);
    await chatApi.sendMessage(active.id, {
      client_message_id: newUuid(),
      ciphertext_b64,
      content_type: "gif",
    });
    void qc.invalidateQueries({ queryKey: ["chat", "messages", activeId] });
  };

  return (
    <div className="flex h-screen flex-col bg-background">
      <Header />
      <div className="flex min-h-0 flex-1">
        <aside className="flex w-80 shrink-0 flex-col border-r border-border">
          <div className="flex items-center gap-2 border-b border-border p-3">
            <MessageSquare className="h-4 w-4" />
            <h1 className="text-sm font-semibold">Чаты</h1>
            <SectionHelpTooltip label="Справка по модулю чата">
              <p className="font-medium">Чат GeoPulseML</p>
              <p>Личные и групповые чаты со сквозным шифрованием. Сервер не видит текст сообщений.</p>
              <ul className="list-disc space-y-1 pl-4">
                <li>Личный чат — выберите пользователя из списка с поиском.</li>
                <li>Группа — название + участники из того же списка.</li>
                <li>Замок в шапке — E2EE включено.</li>
                <li>Пароль на файл — отдельно от E2EE; передайте вне чата.</li>
                <li>GIF — только локальный каталог контура.</li>
              </ul>
              <p>Новое устройство аннулирует старое; история на новом устройстве без backup не читается.</p>
            </SectionHelpTooltip>
          </div>
          <div className="space-y-3 border-b border-border p-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Личный чат</Label>
              <div className="flex gap-1">
                <Combobox
                  className="h-8 min-w-0 flex-1 text-xs"
                  options={userOptions}
                  value={peerId}
                  onValueChange={(v) => setPeerId(v)}
                  placeholder="Выберите пользователя…"
                  searchPlaceholder="Поиск по имени или логину…"
                  emptyText={directoryQuery.isLoading ? "Загрузка…" : "Никого не найдено"}
                  filterMode="server"
                  onSearchChange={setDirectorySearch}
                  allowClear
                />
                <Button
                  size="sm"
                  className="h-8 shrink-0"
                  disabled={!peerId || createDirectMut.isPending}
                  onClick={() => createDirectMut.mutate()}
                  title="Создать личный чат"
                >
                  +
                </Button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Группа</Label>
              <Input
                placeholder="Название группы"
                value={groupTitle}
                onChange={(e) => setGroupTitle(e.target.value)}
                className="h-8 text-xs"
              />
              <Combobox
                className="h-8 w-full text-xs"
                options={userOptions.filter((o) => !groupMemberIds.includes(o.value))}
                value={undefined}
                onValueChange={(v) => {
                  if (v) toggleGroupMember(v);
                }}
                placeholder="Добавить участника…"
                searchPlaceholder="Поиск участника…"
                emptyText={directoryQuery.isLoading ? "Загрузка…" : "Никого не найдено"}
                filterMode="server"
                onSearchChange={setDirectorySearch}
                allowClear={false}
              />
              {groupMemberIds.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {groupMemberIds.map((id) => {
                    const u = userById.get(id);
                    return (
                      <Badge key={id} variant="secondary" className="gap-1 pr-1 text-[10px]">
                        {u?.display_name ?? id.slice(0, 8)}
                        <button
                          type="button"
                          className="rounded-sm p-0.5 hover:bg-muted-foreground/20"
                          onClick={() => toggleGroupMember(id)}
                          aria-label="Убрать участника"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              )}
              <Button
                size="sm"
                variant="secondary"
                className="h-8 w-full"
                disabled={!groupTitle.trim() || createGroupMut.isPending}
                onClick={() => createGroupMut.mutate()}
              >
                <Users className="mr-1 h-3.5 w-3.5" />
                Создать группу
              </Button>
            </div>
          </div>
          <ScrollArea className="flex-1">
            <ul className="p-2">
              {(convQuery.data ?? []).map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setActiveId(c.id)}
                    className={cn(
                      "mb-1 flex w-full flex-col rounded-md px-2 py-2 text-left text-sm hover:bg-muted",
                      activeId === c.id && "bg-muted",
                    )}
                  >
                    <span className="flex items-center gap-2 font-medium">
                      {c.type === "group" ? <Users className="h-3.5 w-3.5" /> : <MessageSquare className="h-3.5 w-3.5" />}
                      {c.type === "group" ? c.title : directTitle(c)}
                      <Badge variant="outline" className="ml-auto text-[10px]">
                        {c.type === "group" ? "группа" : "1:1"}
                      </Badge>
                    </span>
                    <span className="text-[10px] text-muted-foreground">{c.encryption_protocol}</span>
                  </button>
                </li>
              ))}
            </ul>
          </ScrollArea>
        </aside>

        <main className="flex min-w-0 flex-1 flex-col">
          {active ? (
            <>
              <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold">
                    {active.type === "group" ? active.title : directTitle(active)}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Lock className="h-3 w-3 text-emerald-600" />
                    Сквозное шифрование включено
                    {active.type === "group" ? ` · epoch ${active.sender_key_epoch}` : ""}
                  </div>
                </div>
              </div>
              <ScrollArea className="flex-1 p-4">
                <div className="mx-auto flex max-w-3xl flex-col gap-2">
                  {(messagesQuery.data?.items ?? []).map((m: ChatMessage) => (
                    <div key={m.id} className="rounded-lg border border-border bg-card px-3 py-2 text-sm">
                      <div className="mb-1 text-[10px] text-muted-foreground">
                        {m.sender_id.slice(0, 8)}… · seq {m.seq} · {m.content_type}
                      </div>
                      {m.content_type === "gif" && decoded[m.id]?.startsWith("gif:") ? (
                        <img
                          src={chatApi.gifContentUrl(decoded[m.id].slice(4))}
                          alt="GIF"
                          className="max-h-48 rounded"
                        />
                      ) : (
                        <div className="whitespace-pre-wrap break-words">{decoded[m.id] ?? "…"}</div>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <div className="space-y-2 border-t border-border p-3">
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  <label className="flex items-center gap-2">
                    <Checkbox
                      checked={protectPassword}
                      onCheckedChange={(v) => setProtectPassword(v === true)}
                    />
                    <span>Защитить файл паролем</span>
                  </label>
                  {protectPassword && (
                    <Input
                      type="password"
                      placeholder="Пароль (не в чат)"
                      value={filePassword}
                      onChange={(e) => setFilePassword(e.target.value)}
                      className="h-8 max-w-xs text-xs"
                    />
                  )}
                  {protectPassword && (
                    <span className="text-amber-700 dark:text-amber-400">
                      Пароль не передаётся через чат. Передайте получателю отдельно.
                    </span>
                  )}
                  <Label className="inline-flex cursor-pointer items-center gap-1">
                    <Paperclip className="h-3.5 w-3.5" />
                    Файл
                    <input
                      type="file"
                      className="hidden"
                      onChange={(e) => void onPickFile(e.target.files?.[0] ?? null)}
                    />
                  </Label>
                </div>
                <div className="flex gap-2">
                  <Input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Сообщение (шифруется на клиенте)…"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendMut.mutate();
                      }
                    }}
                  />
                  <Button onClick={() => sendMut.mutate()} disabled={sendMut.isPending || !draft.trim()}>
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Тег GIF"
                    value={gifTag}
                    onChange={(e) => setGifTag(e.target.value)}
                    className="h-8 max-w-[10rem] text-xs"
                  />
                  <div className="flex gap-1 overflow-x-auto">
                    {(gifsQuery.data ?? []).slice(0, 12).map((g) => (
                      <button
                        key={g.id}
                        type="button"
                        className="h-12 w-12 overflow-hidden rounded border"
                        onClick={() => void sendGif(g.id)}
                        title={(g.tags ?? []).join(", ")}
                      >
                        <img src={chatApi.gifContentUrl(g.id)} alt="" className="h-full w-full object-cover" />
                      </button>
                    ))}
                    {(gifsQuery.data ?? []).length === 0 && (
                      <span className="text-xs text-muted-foreground">Нет локальных GIF (загружает админ)</span>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
              Выберите или создайте чат
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
